import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  ClickAwayListener,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { alpha } from '@mui/material/styles';
import { BarChart3, Calendar, CircleDollarSign, Info, Newspaper, PieChart, Tag, TrendingUp } from 'lucide-react';
import { getCountryChipDisplay } from '../shared/securityDisplayPrimitives';
import { formatMovePct, MOVE_PILL_TOOLTIP } from '../watchlist_news/MovePill';
import { marketDataUpdateTooltip } from '../../utils/marketMetricProvenance';
import { controlResetClassName, cx } from '../ui/classNames';

const CLOSE_DELAY_MS = 180;

const PRODUCT_LABELS = Object.freeze({
  yahoo_symbol: 'Market symbol',
  intraday_price: 'Price',
  beta_adtv: 'Beta and trading volume',
  earnings: 'Earnings date',
  news: 'News',
  fx_normalization: 'Currency conversion',
  market_status: 'Market status',
});

const PRODUCT_CADENCE = Object.freeze({
  yahoo_symbol: 'Checked when added; cache maintenance runs weekly',
  intraday_price: 'Usually updated about every 5 min while the market is open',
  beta_adtv: 'Usually updated daily',
  earnings: 'Checked weekly',
  news: 'Usually updated about every 10 min',
  fx_normalization: 'Updated with the price refresh when conversion is needed',
  market_status: 'Updated with the price refresh',
});

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function cleanText(value) {
  return String(value || '').trim();
}

function formatAsOf(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(value) {
  if (!value) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? `${value}T12:00:00`
    : value;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(value, currency = 'USD') {
  const number = finiteNumber(value);
  if (number === null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cleanText(currency).toUpperCase() || 'USD',
      maximumFractionDigits: 2,
    }).format(number);
  } catch (_error) {
    return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

function formatCompactShares(value) {
  const number = finiteNumber(value);
  if (number === null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(number);
  } catch (_error) {
    return number.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
}

function sentenceCase(value) {
  const text = cleanText(value).replaceAll('_', ' ').toLowerCase();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '';
}

function rowDisplayLabel(row) {
  return cleanText(
    row?.ticker || row?.display_identifier || row?.uploaded_ticker || 'Security',
  ) || 'Security';
}

function rowSnapshotProducts(row, loading = false) {
  const products = [];
  const price = finiteNumber(row?.price);
  if (price !== null || loading) {
    products.push({
      product_code: 'intraday_price',
      as_of: row?.quote_timestamp || row?.updated_at || row?.price_asof_date || null,
      loading,
      values: {
        price,
        currency: 'USD',
        source: row?.price_source || null,
      },
    });
  }

  const beta = finiteNumber(row?.beta);
  const adtv = finiteNumber(row?.adtv);
  if (beta !== null || adtv !== null || loading) {
    products.push({
      product_code: 'beta_adtv',
      loading,
      values: {
        beta,
        adtv,
        currency: 'USD',
        beta_as_of: row?.beta_updated_at || null,
        adtv_as_of: row?.adtv_updated_at || null,
      },
    });
  }

  if (row?.next_earnings_date || row?.next_earnings_observed_at || loading) {
    products.push({
      product_code: 'earnings',
      as_of: row?.next_earnings_observed_at || null,
      freshness_code: row?.next_earnings_observed_at && !row?.next_earnings_date
        ? 'checked_no_observation'
        : null,
      loading,
      values: { next_earnings_date: row?.next_earnings_date || null },
    });
  }

  const articleCount = finiteNumber(row?.article_count);
  if (articleCount !== null || row?.article_as_of || loading) {
    products.push({
      product_code: 'news',
      as_of: row?.article_as_of || null,
      freshness_code: articleCount === 0 ? 'no_news_success' : null,
      loading,
      values: { article_count: articleCount },
    });
  }

  const localCurrency = cleanText(row?.local_currency).toUpperCase();
  const fxRate = finiteNumber(row?.fx_rate);
  if (localCurrency || fxRate !== null || loading) {
    products.push({
      product_code: 'fx_normalization',
      as_of: row?.fx_timestamp || null,
      loading,
      values: {
        local_currency: localCurrency || null,
        fx_rate: fxRate,
        fx_required: localCurrency ? localCurrency !== 'USD' : null,
      },
    });
  }

  const marketStatus = cleanText(row?.market_status);
  if (marketStatus || loading) {
    products.push({
      product_code: 'market_status',
      as_of: row?.market_status_asof || null,
      loading,
      values: { market_status: marketStatus || null },
    });
  }
  return products;
}

function productValues(product) {
  return product?.values && typeof product.values === 'object' ? product.values : {};
}

function productValuePresentation(product, row) {
  const values = productValues(product);
  const code = cleanText(product?.product_code);
  if (code === 'intraday_price') {
    const price = formatMoney(values.price ?? row?.price, values.currency || 'USD');
    const source = sentenceCase(values.source || row?.price_source);
    return { primary: price || 'Not available', secondary: source || null };
  }
  if (code === 'beta_adtv') {
    const beta = finiteNumber(values.beta ?? row?.beta);
    const adtv = formatCompactShares(values.adtv ?? row?.adtv);
    const parts = [
      beta === null ? null : `β ${beta.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      adtv ? `${adtv} shares ADTV` : null,
    ].filter(Boolean);
    return { primary: parts.join(' · ') || 'Not available', secondary: null };
  }
  if (code === 'earnings') {
    const date = formatDate(values.next_earnings_date || row?.next_earnings_date);
    if (date) return { primary: date, secondary: 'Next expected report' };
    if (cleanText(product?.freshness_code) === 'checked_no_observation') {
      return { primary: 'No upcoming date reported', secondary: null };
    }
    return { primary: 'Not available', secondary: null };
  }
  if (code === 'news') {
    const count = finiteNumber(values.article_count);
    if (count !== null) {
      return {
        primary: count === 1 ? '1 recent article' : `${count.toLocaleString()} recent articles`,
        secondary: null,
      };
    }
    if (cleanText(product?.freshness_code) === 'no_news_success') {
      return { primary: 'No recent articles', secondary: null };
    }
    return { primary: 'Not available', secondary: null };
  }
  if (code === 'fx_normalization') {
    const localCurrency = cleanText(values.local_currency || row?.local_currency).toUpperCase();
    const rate = finiteNumber(values.fx_rate ?? row?.fx_rate);
    if (values.fx_required === false || localCurrency === 'USD') {
      return { primary: 'USD · no conversion needed', secondary: null };
    }
    if (localCurrency && rate !== null) {
      return {
        primary: `1 ${localCurrency} = ${rate.toLocaleString(undefined, { maximumFractionDigits: 6 })} USD`,
        secondary: null,
      };
    }
    return { primary: localCurrency || 'Not available', secondary: null };
  }
  if (code === 'market_status') {
    const status = sentenceCase(values.market_status || row?.market_status);
    return { primary: status || 'Not available', secondary: null };
  }
  return { primary: 'Not available', secondary: null };
}

function TimestampPill({ asOf, cadence }) {
  const timestamp = formatAsOf(asOf);
  return <Tooltip title={[marketDataUpdateTooltip(asOf), cadence].filter(Boolean).join(' ')} arrow enterDelay={250} placement="top">
    <Typography component="span" variant="caption" color="text.secondary"
      aria-label={timestamp ? `Updated ${timestamp}` : 'Update timestamp not recorded'} tabIndex={0}
      sx={{ display: 'block', fontSize: 10.5, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help' }}>
      {timestamp || 'Not recorded'}
    </Typography>
  </Tooltip>;
}

TimestampPill.propTypes = {
  asOf: PropTypes.string,
  cadence: PropTypes.string,
};

function productSnapshotRows(product, row) {
  const values = productValues(product);
  if (cleanText(product?.product_code) === 'beta_adtv') {
    const beta = finiteNumber(values.beta ?? row?.beta);
    const adtv = formatCompactShares(values.adtv ?? row?.adtv);
    return [
      {
        key: 'beta',
        label: 'Beta',
        primary: beta === null
          ? 'Not available'
          : beta.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        asOf: values.beta_as_of || null,
        loading: Boolean(product?.loading && beta === null),
      },
      {
        key: 'adtv',
        label: 'ADTV (shares)',
        primary: adtv || 'Not available',
        asOf: values.adtv_as_of || null,
        loading: Boolean(product?.loading && !adtv),
      },
    ];
  }
  const value = productValuePresentation(product, row);
  return [{
    key: cleanText(product?.product_code) || 'market-data',
    label: PRODUCT_LABELS[product?.product_code] || 'Market data',
    primary: value.primary,
    secondary: value.secondary,
    asOf: product?.as_of || null,
    loading: Boolean(product?.loading && value.primary === 'Not available'),
  }];
}

function SnapshotValueRow({
  label, primary, secondary, asOf, cadence, loading, prominent = false,
  returnPct = null, localPrice = null, icon: Icon = Info, showTimestamp = true,
}) {
  const numericReturnPct = finiteNumber(returnPct);
  const returnDirection = numericReturnPct === null || numericReturnPct === 0 ? 'neutral' : numericReturnPct > 0 ? 'up' : 'down';
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '18px 86px minmax(0, 1fr)', sm: '18px 108px minmax(0, 1fr) 116px' }, columnGap: 1, rowGap: 0.25,
    alignItems: 'center', px: 1.5, py: prominent ? 1 : 0.85, minHeight: prominent ? 52 : 40, borderTop: '1px solid', borderColor: 'divider' }}>
    <Box component="span" sx={{ color: 'text.secondary', display: 'inline-flex' }}><Icon size={17} aria-hidden="true" /></Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11.5 }}>{label}</Typography>
    <Box sx={{ minWidth: 0, gridColumn: showTimestamp ? undefined : { sm: '3 / -1' } }}>
      <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
        {loading ? <Skeleton variant="text" width={88} height={22} /> : <>
          <Typography variant={prominent ? 'h5' : 'body2'} color={prominent ? 'primary.main' : 'text.primary'}
            sx={{ fontSize: prominent ? 24 : 13, fontWeight: prominent ? 800 : 650, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{primary}</Typography>
          {localPrice && <Typography component="span" data-testid="local-currency-price" variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{localPrice}</Typography>}
          {prominent && numericReturnPct !== null && <Tooltip title={MOVE_PILL_TOOLTIP} arrow>
            <Stack component="span" direction="row" spacing={0} alignItems="center" data-testid="latest-price-return"
              aria-label={`${formatMovePct(numericReturnPct)} current price versus previous day close`}
              sx={{ color: returnDirection === 'up' ? 'success.main' : returnDirection === 'down' ? 'error.main' : 'text.secondary', fontWeight: 800, fontSize: '0.75rem', lineHeight: 1, whiteSpace: 'nowrap' }}>
              {returnDirection === 'up' && <ArrowDropUpIcon data-testid="latest-price-return-up" sx={{ fontSize: 18 }} />}
              {returnDirection === 'down' && <ArrowDropDownIcon data-testid="latest-price-return-down" sx={{ fontSize: 18 }} />}
              <Box component="span">{formatMovePct(numericReturnPct)}</Box>
            </Stack>
          </Tooltip>}
        </>}
      </Stack>
      {secondary && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10.5, mt: 0.25 }}>{secondary}</Typography>}
    </Box>
    {showTimestamp && <Box sx={{ gridColumn: { xs: '3', sm: 'auto' }, minWidth: 0 }}>
      {loading ? <Skeleton variant="text" width={100} height={16} /> : <TimestampPill asOf={asOf} cadence={cadence} />}
    </Box>}
  </Box>;
}

SnapshotValueRow.propTypes = {
  label: PropTypes.string.isRequired,
  primary: PropTypes.string.isRequired,
  secondary: PropTypes.string,
  asOf: PropTypes.string,
  cadence: PropTypes.string,
  loading: PropTypes.bool,
  prominent: PropTypes.bool,
  returnPct: PropTypes.number,
  localPrice: PropTypes.string,
  icon: PropTypes.elementType,
  showTimestamp: PropTypes.bool,
};

function ProductSnapshotRow({ product, row }) {
  const cadence = PRODUCT_CADENCE[product?.product_code];
  return (
    <>
      {productSnapshotRows(product, row).map((snapshot) => (
        <SnapshotValueRow
          key={snapshot.key}
          label={snapshot.label}
          icon={snapshot.key === 'beta' ? TrendingUp : snapshot.key === 'adtv' ? PieChart : product.product_code === 'earnings' ? Calendar : product.product_code === 'news' ? Newspaper : product.product_code === 'fx_normalization' ? CircleDollarSign : Tag}
          primary={snapshot.primary}
          secondary={snapshot.secondary}
          asOf={snapshot.asOf}
          cadence={cadence}
          loading={snapshot.loading}
        />
      ))}
    </>
  );
}

ProductSnapshotRow.propTypes = {
  product: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
};

function SecurityDetailsContent({ row, dataLoading = false, identityDetails = [] }) {
  const details = Object.fromEntries((identityDetails || []).map(({ label, value }) => [cleanText(label), cleanText(value)]));
  const present = (value) => value && value !== '—' ? value : '';
  const ticker = present(details.Ticker) || rowDisplayLabel(row);
  const company = present(details.Company) || cleanText(row.company_name || row.yf_longname || row.yf_shortname);
  const country = present(details.Country) || cleanText(row.country);
  const countryDisplay = country ? getCountryChipDisplay(country) : null;
  const calendarDate = present(details['Calendar date']);
  const products = rowSnapshotProducts(row, dataLoading);
  const priceProduct = products.find((product) => product.product_code === 'intraday_price');
  const marketProduct = products.find((product) => product.product_code === 'market_status');
  const market = marketProduct ? productValuePresentation(marketProduct, row) : null;
  const symbol = cleanText(row.yf_symbol);
  const submitted = cleanText(row.inbound_id_value) || present(details['Submitted identifier']);
  const submittedLabel = { bloomberg_ticker: 'Bloomberg ID', isin: 'ISIN', cusip: 'CUSIP', sedol: 'SEDOL', ticker: 'Submitted ticker' }[cleanText(row.inbound_id_type).toLowerCase()] || 'Submitted ID';
  const localCurrency = cleanText(row.local_currency).toUpperCase();
  const localPrice = localCurrency && localCurrency !== 'USD' ? formatMoney(row.local_price, localCurrency) : null;
  const remainingProducts = products.filter((product) => !['intraday_price', 'market_status', 'yahoo_symbol'].includes(product.product_code));

  return <Box sx={{ width: { xs: 330, sm: 500 }, maxWidth: 'calc(100vw - 24px)' }}>
    <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="h5" sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, minWidth: 0, overflowWrap: 'anywhere' }}>{ticker}</Typography>
        {market?.primary && market.primary !== 'Not available' && <Tooltip title={marketDataUpdateTooltip(marketProduct?.as_of)} arrow>
          <Chip size="small" variant="outlined" label={market.primary} sx={{ height: 24, flexShrink: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', bgcolor: 'action.hover', borderColor: 'divider' }} />
        </Tooltip>}
      </Stack>
      {company && company !== ticker && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{company}</Typography>}
      {(country || symbol && symbol !== ticker || calendarDate) && <Box sx={{ mt: 1.1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, minWidth: 0 }}>
        {country && <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 11.5 }}>{countryDisplay?.flag && <span aria-hidden="true">{countryDisplay.flag}</span>}{country}</Typography>}
        {symbol && symbol !== ticker && <Tooltip title={[marketDataUpdateTooltip(row.yahoo_cache_last_checked), PRODUCT_CADENCE.yahoo_symbol].join(' ')} arrow>
          <Typography variant="caption" color="text.secondary" tabIndex={0} sx={{ pl: country ? 1.25 : 0, borderLeft: country ? '1px solid' : 0, borderColor: 'divider', fontSize: 11.5 }}>{symbol}</Typography>
        </Tooltip>}
        {calendarDate && <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: 11.5 }}>{formatDate(calendarDate) || calendarDate}</Typography>}
      </Box>}
    </Box>
    <Box sx={{ maxHeight: 'min(400px, calc(100vh - 160px))', overflowY: 'auto', overflowX: 'hidden' }}>
      {priceProduct && <SnapshotValueRow label="Price" icon={BarChart3}
        primary={productValuePresentation(priceProduct, row).primary} localPrice={localPrice}
        asOf={priceProduct.as_of} cadence={PRODUCT_CADENCE.intraday_price}
        loading={Boolean(priceProduct.loading && finiteNumber(row.price) === null)} prominent returnPct={row?._newsItem?.return_pct} />}
      {submitted && <SnapshotValueRow label={submittedLabel} primary={submitted} icon={Tag} showTimestamp={false} />}
      {remainingProducts.map((product) => <ProductSnapshotRow key={product.product_code} product={product} row={row} />)}
      {!priceProduct && !remainingProducts.length && <Typography variant="body2" color="text.secondary" sx={{ px: 1.75, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>Detailed market-data status is temporarily unavailable.</Typography>}
    </Box>
  </Box>;
}

SecurityDetailsContent.propTypes = {
  row: PropTypes.object.isRequired,
  dataLoading: PropTypes.bool,
  identityDetails: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  })),
};

export default function SecurityDetailsPopover({
  row,
  loading = false,
  displayLabel: displayLabelOverride = '',
  labelTooltip = '',
  identityDetails = [],
  triggerAriaLabel = '',
  renderTrigger = null,
}) {
  const popoverId = useId();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pinned, setPinned] = useState(false);
  const closeTimerRef = useRef(null);
  const displayLabel = cleanText(displayLabelOverride) || rowDisplayLabel(row);
  const hasIdentityDetails = Array.isArray(identityDetails) && identityDetails.length > 0;
  const open = Boolean(anchorEl);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const close = useCallback(() => {
    clearCloseTimer();
    setPinned(false);
    setAnchorEl(null);
  }, [clearCloseTimer]);
  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    if (pinned) return;
    closeTimerRef.current = window.setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  }, [clearCloseTimer, pinned]);
  const preview = useCallback((event) => {
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  }, [clearCloseTimer]);
  const handleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    clearCloseTimer();
    if (open && pinned) {
      close();
      return;
    }
    setAnchorEl(event.currentTarget);
    setPinned(true);
  }, [clearCloseTimer, close, open, pinned]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  if (!hasIdentityDetails || row?._isPlaceholder) {
    return <span title={labelTooltip || undefined}>{displayLabel}</span>;
  }

  const previewProps = {
    'aria-haspopup': 'dialog',
    'aria-controls': open ? popoverId : undefined,
    'aria-expanded': open ? 'true' : 'false',
    title: labelTooltip || undefined,
    onMouseEnter: preview,
    onMouseLeave: scheduleClose,
    onFocus: preview,
    onBlur: scheduleClose,
    onKeyDown: (event) => {
      if (event.key === 'Escape') close();
    },
  };
  const triggerStatus = 'Open market data summary';
  // The default trigger is plain markup, so it can sit in a DataTable cell;
  // the details panel is an MUI Popper rendered outside the table.
  return (
    <ClickAwayListener onClickAway={() => { if (open) close(); }}>
      <span className="inline-flex min-w-0 max-w-full">
        {/* A custom trigger retains its primary action; the ordinary table trigger pins the preview. */}
        {renderTrigger ? renderTrigger({ ...previewProps, onClick: close }) : <button
          type="button"
          aria-label={triggerAriaLabel || `Market data details for ${displayLabel}: ${triggerStatus}`}
          {...previewProps}
          onClick={handleClick}
          className={cx(
            controlResetClassName,
            'inline-flex min-w-0 max-w-full cursor-pointer items-center gap-1 rounded border-0 bg-transparent',
            'p-0 text-left text-inherit focus:outline-none focus-visible:outline focus-visible:outline-2',
            'focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <Info
            aria-hidden="true"
            size={15}
            className="shrink-0 text-content-secondary"
          />
        </button>}
        <Popper
          id={popoverId}
          open={open}
          anchorEl={anchorEl}
          placement="right-start"
          modifiers={[
            { name: 'offset', options: { offset: [0, 10] } },
            { name: 'flip', enabled: true },
            { name: 'preventOverflow', enabled: true, options: { padding: 12 } },
          ]}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Paper
            role="dialog"
            aria-label={`Security and market data details for ${displayLabel}`}
            elevation={10}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            onFocusCapture={clearCloseTimer}
            onBlurCapture={scheduleClose}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 18px 44px rgba(15, 23, 42, 0.24)',
            }}
          >
            <SecurityDetailsContent
              row={row}
              dataLoading={loading}
              identityDetails={identityDetails}
            />
          </Paper>
        </Popper>
      </span>
    </ClickAwayListener>
  );
}

SecurityDetailsPopover.propTypes = {
  row: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  displayLabel: PropTypes.string,
  labelTooltip: PropTypes.string,
  identityDetails: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  })),
  triggerAriaLabel: PropTypes.string,
  renderTrigger: PropTypes.func,
};
